import { useTranslation } from "react-i18next";
import React, { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, AlertCircle } from "lucide-react";
import { SelectedProductItem } from "@/components/products/SelectedProductItem";
import { Product, ProductSelection } from "@/types/product";
import { useProductsLookup } from "@/hooks/use-products";
import { useProductSummary } from "@/hooks/use-product-summary";


interface CartItemListProps {
  /** The products currently in the cart. */
  selectedProducts: ProductSelection[];
  /** Called when the user changes an item's quantity. Omit for readOnly. */
  onQuantityChange?: (productId: string, quantity: number) => void;
  /** Called when the user removes an item. Omit for readOnly. */
  onRemove?: (productId: string) => void;
  /** Called when the user clicks "Clear all". Omit to hide the button. */
  onClearAll?: () => void;
  /** Show dollar amounts next to each item. */
  showPricing?: boolean;
  /** When true, hides all edit controls (qty steppers, remove, clear). */
  readOnly?: boolean;
  /** Additional products from props (e.g. from catalog browsing) to merge with looked-up data. */
  products?: Product[];
  /** Whether the products prop is still loading. */
  productsLoading?: boolean;
}

export const CartItemList: React.FC<CartItemListProps> = ({
  selectedProducts,
  onQuantityChange,
  onRemove,
  onClearAll,
  showPricing = false,
  readOnly = false,
  products: propProducts = [],
  productsLoading = false,
}) => {
  // Look up full product details for everything in the cart
  const { t } = useTranslation();
  const productIds = useMemo(
    () => selectedProducts.map((sp) => sp.productId),
    [selectedProducts],
  );

  const { products: fetchedProducts, isLoading: isFetchingProducts } =
    useProductsLookup(productIds);

  // Merge prop products + fetched products into a lookup map
  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    propProducts.forEach((p) => map.set(p.uid, p));
    fetchedProducts.forEach((p) => map.set(p.uid, p));
    return map;
  }, [propProducts, fetchedProducts]);

  const isLoading = productsLoading || isFetchingProducts;

  // Summary stats
  const summary = useProductSummary({
    selectedProducts: selectedProducts.map((p) => ({
      quantity: p.quantity,
      unitPrice: "unitPrice" in p ? p.unitPrice : undefined,
    })),
    showPricing,
  });

  const handleQuantityChange = useCallback(
    (productId: string, quantity: number) => {
      onQuantityChange?.(productId, quantity);
    },
    [onQuantityChange],
  );

  const handleRemove = useCallback(
    (productId: string) => {
      onRemove?.(productId);
    },
    [onRemove],
  );

  if (selectedProducts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Clear all button — count is shown by parent (CartPanel badge) */}
      {!readOnly && onClearAll && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="text-xs text-muted-foreground hover:text-destructive h-6 px-2"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            {t("components.orders.cartItemList.clearAll")}
          </Button>
        </div>
      )}

      {/* Product items */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {t("components.orders.cartItemList.loadingProducts")}
          </div>
        ) : (
          selectedProducts.map((sp) => {
            const product = productMap.get(sp.productId);
            if (!product) return null;

            return (
              <SelectedProductItem
                key={sp.productId}
                selectedProduct={sp}
                product={product}
                onQuantityChange={(qty) =>
                  handleQuantityChange(sp.productId, qty)
                }
                onRemove={() => handleRemove(sp.productId)}
                showPricing={showPricing}
                hideControls={readOnly}
              />
            );
          })
        )}
      </div>

      {showPricing && summary.hasMissingPrices && (
        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-md p-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {t("components.orders.cartItemList.pricingWillBeCalculated")}
        </div>
      )}
    </div>
  );
};
